import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../ui/button';

interface DisplayField {
    key: string;
    label: string;
}

interface ContextListViewProps {
    data: any[] | undefined;
    displayFields: DisplayField[];
    emptyMessage?: string;
    description?: string;
    onRemoveItem?: (index: number) => void;
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
    nordinsight: 'NordInsight Registry',
    url: 'URL',
    policy: 'Internal Policy',
};

const formatDisplayValue = (key: string, value: unknown): string | null => {
    if (value === undefined || value === null || value === '') return null;

    if (key === 'sourceType' && typeof value === 'string') {
        return SOURCE_TYPE_LABELS[value] || value;
    }

    return String(value);
};

export const ContextListView: React.FC<ContextListViewProps> = ({
    data,
    displayFields,
    emptyMessage = 'No items available.',
    description,
    onRemoveItem,
}) => {
    const items = Array.isArray(data) ? data : [];

    const visibleItems = items.filter((item) =>
        displayFields.some(
            (df) => item?.[df.key] !== undefined && item[df.key] !== ''
        )
    );

    if (visibleItems.length === 0) {
        return null;
    }

    return (
        <div
            className={cn(
                'rounded-xl border p-4 shadow-sm space-y-2',
                'border-border/45 bg-background/45 dark:border-white/10 dark:bg-[#10161F]/80'
            )}
        >
            {description && (
                <p className="text-xs text-muted-foreground/70">
                    {description}
                </p>
            )}
            {visibleItems.map((item) => {
                const originalIndex = items.indexOf(item);
                const fields = displayFields
                    .map((df) => ({
                        label: df.label,
                        value: formatDisplayValue(df.key, item?.[df.key]),
                    }))
                    .filter((f) => f.value !== null);

                return (
                    <div
                        key={originalIndex}
                        className="flex items-center gap-2 rounded-lg border border-border/30 bg-muted/30 px-3 py-2 text-sm"
                    >
                        <div className="flex flex-1 flex-wrap gap-x-4 gap-y-1">
                            {fields.map((f) => (
                                <span key={f.label}>
                                    <span className="font-medium text-muted-foreground mr-1">
                                        {f.label}:
                                    </span>
                                    <span>{f.value}</span>
                                </span>
                            ))}
                        </div>
                        {onRemoveItem && (
                            <Button
                                variant="ghost"
                                size="sm"
                                type="button"
                                onClick={() => onRemoveItem(originalIndex)}
                                className="shrink-0 rounded-lg bg-transparent text-muted-foreground/50 hover:bg-destructive/10 hover:text-destructive h-7 w-7 p-0"
                            >
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
